import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  Divider,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Add,
  Visibility,
  School,
  Person,
  Phone,
  Email,
  LocationOn,
  Event,
  WhatsApp,
  FamilyRestroom,
  FilterList,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function AdminStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [filteredByApiCount, setFilteredByApiCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name_with_initials: '',
    full_name: '',
    student_id: '',
    phone: '',
    whatsapp_number: '',
    address: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_email: '',
    username: '',
    password: '',
    password_confirmation: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [grades, setGrades] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [selectedGrade, selectedSubject]);

  useEffect(() => {
    const filtered = students.filter(
      (student) =>
        student.student_index?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.name_with_initials?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStudents(filtered);
    setPage(0);
  }, [searchTerm, students]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/students/filters/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrades(response.data.grades || []);
      setAvailableSubjects(response.data.subjects || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Fallback to default values if API fails
      setGrades([6, 7, 8, 9, 10, 11]);
      setAvailableSubjects(['Mathematics', 'Science']);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedGrade) {
        params.grade = selectedGrade;
      }
      if (selectedSubject) {
        params.subject = selectedSubject;
      }
      
      const response = await axios.get(`${API_URL}/students`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      // Parse subjects JSON string to array
      const studentsWithParsedSubjects = response.data.data.map(student => ({
        ...student,
        subjects: student.subjects ? (typeof student.subjects === 'string' ? JSON.parse(student.subjects) : student.subjects) : []
      }));
      setStudents(studentsWithParsedSubjects);
      setFilteredStudents(studentsWithParsedSubjects);
      setTotalStudents(response.data.total);
      setFilteredByApiCount(response.data.filtered);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (student) => {
    // Ensure subjects is an array
    const studentWithParsedSubjects = {
      ...student,
      subjects: student.subjects ? (typeof student.subjects === 'string' ? JSON.parse(student.subjects) : student.subjects) : []
    };
    setSelectedStudent(studentWithParsedSubjects);
    setOpenDetails(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditDialog = (student) => {
    setSelectedStudent(student);
    setEditFormData({
      name_with_initials: student.name_with_initials || '',
      full_name: student.full_name || '',
      student_id: student.student_id || student.student_index || '',
      phone: student.phone || '',
      whatsapp_number: student.whatsapp_number || student.whatsapp || '',
      address: student.address || '',
      guardian_name: student.guardian_name || '',
      guardian_phone: student.guardian_phone || '',
      guardian_email: student.guardian_email || '',
      username: student.user?.username || '',
      password: '',
      password_confirmation: '',
    });
    setEditFormErrors({});
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedStudent(null);
    setEditFormData({
      name_with_initials: '',
      full_name: '',
      student_id: '',
      phone: '',
      whatsapp_number: '',
      address: '',
      guardian_name: '',
      guardian_phone: '',
      guardian_email: '',
      username: '',
      password: '',
      password_confirmation: '',
    });
    setEditFormErrors({});
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value });
    if (editFormErrors[field]) {
      setEditFormErrors({ ...editFormErrors, [field]: null });
    }
  };

  const handleUpdateStudent = async () => {
    const errors = {};
    
    if (!editFormData.name_with_initials.trim()) {
      errors.name_with_initials = 'Name with initials is required';
    }
    if (!editFormData.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }
    if (!editFormData.student_id.trim()) {
      errors.student_id = 'Student ID is required';
    }
    if (!editFormData.username.trim()) {
      errors.username = 'Username is required';
    }
    if (editFormData.password && editFormData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (editFormData.password !== editFormData.password_confirmation) {
      errors.password_confirmation = 'Passwords do not match';
    }

    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = { ...editFormData };
      
      // Only include password if provided
      if (!updateData.password) {
        delete updateData.password;
        delete updateData.password_confirmation;
      }
      
      await axios.put(`${API_URL}/students/${selectedStudent.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert('Student updated successfully!');
      fetchStudents();
      handleCloseEditDialog();
    } catch (error) {
      if (error.response?.data?.errors) {
        setEditFormErrors(error.response.data.errors);
      } else {
        alert('Failed to update student. Please try again.');
      }
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete ${studentName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert('Student deleted successfully!');
      fetchStudents();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete student.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <School sx={{ fontSize: { xs: 32, md: 40 }, color: '#5c55f3', mr: 2 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: '#2d3748',
                fontSize: { xs: '1.5rem', md: '2.125rem' },
              }}>
                Student Management
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #7c75ff 0%, #5c55f3 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(92, 85, 243, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              onClick={() => navigate('/register')}
            >
              Add New Student
            </Button>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Index, Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Grade</InputLabel>
                <Select
                  value={selectedGrade}
                  label="Filter by Grade"
                  onChange={(e) => setSelectedGrade(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Grades</em>
                  </MenuItem>
                  {grades.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  label="Filter by Subject"
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Subjects</em>
                  </MenuItem>
                  {availableSubjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                {(selectedGrade || selectedSubject) && (
                  <>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Active Filters:
                    </Typography>
                    {selectedGrade && (
                      <Chip
                        label={`Grade ${selectedGrade}`}
                        onDelete={() => setSelectedGrade('')}
                        sx={{
                          bgcolor: '#e8eaf6',
                          color: '#5c55f3',
                          fontWeight: 'bold',
                        }}
                      />
                    )}
                    {selectedSubject && (
                      <Chip
                        label={selectedSubject}
                        onDelete={() => setSelectedSubject('')}
                        sx={{
                          bgcolor: '#f3e5f5',
                          color: '#7c75ff',
                          fontWeight: 'bold',
                        }}
                      />
                    )}
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedGrade('');
                        setSelectedSubject('');
                      }}
                      sx={{ ml: 'auto', color: '#64748b' }}
                    >
                      Clear All
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)', 
                textAlign: 'center',
                border: '2px solid #5c55f3',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#5c55f3' }}>
                  {totalStudents}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Students
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', 
                textAlign: 'center',
                border: '2px solid #ff9800',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff9800' }}>
                  {filteredByApiCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Filtered by Grade/Subject
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', 
                textAlign: 'center',
                border: '2px solid #7c75ff',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#7c75ff' }}>
                  {filteredStudents.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  After Search Filter
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', 
                textAlign: 'center',
                border: '2px solid #4caf50',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#4caf50' }}>
                  {new Set(students.map((s) => s.class_id)).size}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Classes
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Index</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Grade</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Subjects</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Phone</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>WhatsApp</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Gender</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>Guardian</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((student) => (
                    <TableRow 
                      key={student.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(92, 85, 243, 0.05)',
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={student.student_id || student.student_index}
                          sx={{
                            background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              mr: 2, 
                              background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                              width: { xs: 32, md: 40 },
                              height: { xs: 32, md: 40 },
                              fontSize: { xs: '0.875rem', md: '1rem' },
                            }}
                          >
                            {student.name_with_initials?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                              {student.name_with_initials}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {student.full_name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<School sx={{ fontSize: 16 }} />}
                          label={student.grade_display || `Grade ${student.grade || 'N/A'}`}
                          size="small"
                          sx={{
                            bgcolor: '#e8eaf6',
                            color: '#5c55f3',
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {student.subjects && student.subjects.length > 0 ? (
                            student.subjects.slice(0, 2).map((subject, index) => (
                              <Chip
                                key={index}
                                label={subject}
                                size="small"
                                sx={{
                                  bgcolor: '#f3e5f5',
                                  color: '#7c75ff',
                                  fontSize: '0.75rem',
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="textSecondary">N/A</Typography>
                          )}
                          {student.subjects && student.subjects.length > 2 && (
                            <Chip
                              label={`+${student.subjects.length - 2}`}
                              size="small"
                              sx={{
                                bgcolor: '#e8eaf6',
                                color: '#5c55f3',
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{student.phone || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2">{student.whatsapp_number || student.whatsapp || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={student.gender}
                          size="small"
                          sx={{
                            bgcolor: student.gender === 'Male' ? '#e3f2fd' : '#fce4ec',
                            color: student.gender === 'Male' ? '#1976d2' : '#c2185b',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {student.guardian_phone || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          sx={{
                            color: '#5c55f3',
                            '&:hover': {
                              backgroundColor: 'rgba(92, 85, 243, 0.1)',
                            },
                          }}
                          onClick={() => handleViewDetails(student)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          sx={{
                            color: '#4caf50',
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            },
                          }}
                          onClick={() => handleOpenEditDialog(student)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          sx={{
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            },
                          }}
                          onClick={() => handleDeleteStudent(student.id, student.name_with_initials)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredStudents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Details Dialog */}
        <Dialog 
          open={openDetails} 
          onClose={() => setOpenDetails(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              background: 'linear-gradient(to bottom, rgba(92, 85, 243, 0.05) 0%, white 100%)',
            },
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 'bold',
          }}>
            <Person />
            Student Details
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedStudent && (
              <Box>
                {/* Personal Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: '#5c55f3', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Person /> Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Student ID
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#5c55f3', mt: 0.5 }}>
                          {selectedStudent.student_id || selectedStudent.student_index}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Name with Initials
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedStudent.name_with_initials}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Full Name
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedStudent.full_name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Event fontSize="small" /> Date of Birth
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {selectedStudent.date_of_birth ? new Date(selectedStudent.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Gender
                        </Typography>
                        <Chip
                          label={selectedStudent.gender}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: selectedStudent.gender === 'Male' ? '#e3f2fd' : '#fce4ec',
                            color: selectedStudent.gender === 'Male' ? '#1976d2' : '#c2185b',
                            fontWeight: 'bold',
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Academic Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: '#5c55f3', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <School /> Academic Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#e8eaf6', 
                        borderRadius: 1,
                        border: '2px solid #5c55f3',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Grade
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#5c55f3', mt: 0.5 }}>
                          {selectedStudent.grade_display || `Grade ${selectedStudent.grade}` || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          O/L Year
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          20{selectedStudent.ol_year || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f3e5f5', 
                        borderRadius: 1,
                        border: '1px solid #7c75ff',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                          Subjects
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {selectedStudent.subjects && selectedStudent.subjects.length > 0 ? (
                            selectedStudent.subjects.map((subject, index) => (
                              <Chip
                                key={index}
                                label={subject}
                                sx={{
                                  bgcolor: '#7c75ff',
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">No subjects assigned</Typography>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Primary Class
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedStudent.class?.name || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Event fontSize="small" /> Admission Date
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {selectedStudent.admission_date ? new Date(selectedStudent.admission_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Contact Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: '#5c55f3', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Phone /> Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#e3f2fd', 
                        borderRadius: 1,
                        border: '1px solid #2196f3',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone fontSize="small" /> Phone Number
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#2196f3', mt: 0.5 }}>
                          {selectedStudent.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#e8f5e9', 
                        borderRadius: 1,
                        border: '1px solid #4caf50',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WhatsApp fontSize="small" /> WhatsApp
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#4caf50', mt: 0.5 }}>
                          {selectedStudent.whatsapp_number || selectedStudent.whatsapp || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn fontSize="small" /> Address
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {selectedStudent.address || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Guardian Information */}
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: '#5c55f3', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <FamilyRestroom /> Guardian Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#fff3e0', 
                        borderRadius: 1,
                        border: '1px solid #ff9800',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Guardian Name
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedStudent.guardian_name || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone fontSize="small" /> Guardian Phone
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {selectedStudent.guardian_phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email fontSize="small" /> Guardian Email
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {selectedStudent.guardian_email || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setOpenDetails(false)}
              sx={{
                color: '#5c55f3',
                '&:hover': {
                  bgcolor: 'rgba(92, 85, 243, 0.1)',
                },
              }}
            >
              Close
            </Button>
            <Button 
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                setOpenDetails(false);
                handleOpenEditDialog(selectedStudent);
              }}
              sx={{
                background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c75ff 0%, #5c55f3 100%)',
                },
              }}
            >
              Edit Student
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Student Dialog */}
        <Dialog 
          open={openEditDialog} 
          onClose={handleCloseEditDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Edit sx={{ mr: 1, color: '#5c55f3' }} />
              Edit Student
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name with Initials *"
                  value={editFormData.name_with_initials}
                  onChange={(e) => handleEditInputChange('name_with_initials', e.target.value)}
                  error={!!editFormErrors.name_with_initials}
                  helperText={editFormErrors.name_with_initials}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  value={editFormData.full_name}
                  onChange={(e) => handleEditInputChange('full_name', e.target.value)}
                  error={!!editFormErrors.full_name}
                  helperText={editFormErrors.full_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Student ID *"
                  value={editFormData.student_id}
                  onChange={(e) => handleEditInputChange('student_id', e.target.value)}
                  error={!!editFormErrors.student_id}
                  helperText={editFormErrors.student_id}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editFormData.phone}
                  onChange={(e) => handleEditInputChange('phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  value={editFormData.whatsapp_number}
                  onChange={(e) => handleEditInputChange('whatsapp_number', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Guardian Name"
                  value={editFormData.guardian_name}
                  onChange={(e) => handleEditInputChange('guardian_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Guardian Phone"
                  value={editFormData.guardian_phone}
                  onChange={(e) => handleEditInputChange('guardian_phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Guardian Email"
                  type="email"
                  value={editFormData.guardian_email}
                  onChange={(e) => handleEditInputChange('guardian_email', e.target.value)}
                  error={!!editFormErrors.guardian_email}
                  helperText={editFormErrors.guardian_email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={editFormData.address}
                  onChange={(e) => handleEditInputChange('address', e.target.value)}
                />
              </Grid>
              
              {/* Account Information Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Account Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username *"
                  value={editFormData.username}
                  onChange={(e) => handleEditInputChange('username', e.target.value)}
                  error={!!editFormErrors.username}
                  helperText={editFormErrors.username}
                />
              </Grid>
              
              {/* Password Change Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Change Password (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => handleEditInputChange('password', e.target.value)}
                  error={!!editFormErrors.password}
                  helperText={editFormErrors.password || "Leave blank to keep current password"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={editFormData.password_confirmation}
                  onChange={(e) => handleEditInputChange('password_confirmation', e.target.value)}
                  error={!!editFormErrors.password_confirmation}
                  helperText={editFormErrors.password_confirmation}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseEditDialog}
              sx={{
                color: '#64748b',
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateStudent}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c75ff 0%, #5c55f3 100%)',
                },
              }}
            >
              Update Student
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default AdminStudents;
